import { memo, useMemo } from "react";
import "./AlertMessage.css";
import LinkAndCloseComponent from "./components/LinkAndCloseComponent";
import { Icon } from "../icons/Icon";
import type { IconName } from "../icons/icons";

export interface AlertMessageProps {
  titleLabel: string;
  textLabel: string;
  contentType: "success" | "error" | "warning" | "info";
  onClose?: () => void;
  links?: string | string[] | undefined;
  width?: number | string;
  formatType?: "feedback" | "persistent";
}

const AlertMessage = ({
  titleLabel,
  textLabel,
  contentType,
  links,
  formatType = "persistent",
  onClose = () => {},
}: AlertMessageProps) => {
  const messageIcon = useMemo(() => {
    switch (contentType) {
      case "error":
        return {
          icon: "errorX",
          color: "ib-pt-color-red-800",
        };
      case "info":
        return {
          icon: "infoCircle",
          color: "pt-color-blue-info-500",
        };
      case "success":
        return {
          icon: "success",
          color: "ib-pt-color-green-800",
        };
      case "warning":
        return {
          icon: "warningTriangle",
          color: "ib-pt-color-yellow-800",
        };
      default:
        return {
          icon: "infoCircle",
          color: "pt-color-blue-info-500",
        };
    }
  }, [contentType]);

  return (
    <div
      data-testid="coloured-message"
      className={`coloured-message ${contentType}-${formatType} w-fit`}
    >
      <div className="w-full md:w-fit flex flex-row justify-between gap-x-4 items-center md:items-start">
        <div className="h-[16px] w-[16px]" data-testid="icon-svg">
          <Icon
            name={messageIcon.icon as IconName}
            className={`text-${messageIcon.color} size-4`}
          />
        </div>
        <div className="message-title md:hidden">{titleLabel}</div>
        <LinkAndCloseComponent
          contentType={contentType}
          links={links}
          onClick={onClose}
          mobileView
        />
      </div>
      <div className="flex flex-col gap-y-2 grow md:transform translate-y-[-4px]">
        <div className="message-title max-md:hidden">{titleLabel}</div>

        <div className="text-left font-normal text-[14px] mr-6">
          {textLabel}
        </div>
      </div>
      <LinkAndCloseComponent
        contentType={contentType}
        links={links}
        onClick={onClose}
      />
    </div>
  );
};

export default memo(AlertMessage);
